import { useCallback, useEffect, useState } from "react";
import { Settings } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { log } from "../../lib/log";
import { METHODS } from "./_shared";

// ---------------------------------------------------------------------------
// Settings Tab — owner-only. Global 2FA toggle (soft-launch ramp).
// ---------------------------------------------------------------------------
export function SettingsTab() {
  const { apiFetch } = useAuth();
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch("/api/admin/settings");
      setSettings(d);
    } catch (e) {
      setMsg(`Feil: ${e?.message || e}`);
    }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const setRequire2FA = async (value) => {
    setBusy(true); setMsg(null);
    try {
      const d = await apiFetch("/api/admin/settings", {
        method: "POST",
        body: JSON.stringify({ globally_require_2fa: value }),
      });
      setSettings(d);
      setMsg(value
        ? "✓ 2FA er nå påkrevd for alle brukere. Ved neste innlogging må de skrive inn en kode fra e-posten."
        : "✓ 2FA er nå valgfritt. Brukere kan slå det på selv i kontoinnstillingene.");
    } catch (e) {
      setMsg(`Feil: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  if (!settings) {
    return <div className="mt-8 p-12 text-center text-sm text-[var(--srs-ink-dim)]" data-testid="settings-loading">Loading…</div>;
  }

  const on = !!settings.globally_require_2fa;

  return (
    <div className="mt-8 space-y-6" data-testid="settings-tab">
      <div>
        <div className="text-[10px] tracking-[0.3em] font-bold text-[var(--srs-ink-faint)] mb-2">// PLATFORM SETTINGS</div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[var(--srs-ink)]">
          Soft-launch controls
        </h2>
        <p className="mt-2 text-sm text-[var(--srs-ink-dim)] max-w-2xl">
          Settings that affect every user on the platform. Use carefully.
        </p>
      </div>

      {/* Global 2FA card */}
      <div className="border border-[var(--srs-line)] bg-[var(--srs-surface)] rounded-lg p-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)] mb-2">TWO-FACTOR AUTHENTICATION</div>
            <h3 className="text-lg font-bold text-[var(--srs-ink)] mb-2">
              {on ? "Required for all users" : "Optional (opt-in)"}
            </h3>
            <p className="text-sm text-[var(--srs-ink-dim)] leading-relaxed">
              {on
                ? "Every user — owner and subscribers — must enter a 6-digit code from email on every web login. Extension stays trusted after first 2FA on each install."
                : "2FA is off by default. Each user can opt in from their account page. Flip this to enforce 2FA platform-wide when you're ready."}
            </p>
          </div>
          <button
            onClick={() => setRequire2FA(!on)}
            disabled={busy}
            className={on ? "px-5 py-2.5 text-[11px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] hover:border-[var(--srs-ink)] transition-colors disabled:opacity-50 rounded"
                          : "px-5 py-2.5 text-[11px] tracking-[0.2em] font-bold uppercase bg-[var(--srs-sun)] text-white hover:bg-[var(--srs-sun-deep)] transition-colors disabled:opacity-50 rounded"}
            data-testid="toggle-require-2fa"
          >
            {busy ? "Saving…" : on ? "Disable global 2FA" : "Require for everyone →"}
          </button>
        </div>
        {msg && (
          <div className={`mt-4 text-sm leading-relaxed ${msg.startsWith("Feil") ? "text-[var(--srs-warn)]" : "text-[var(--srs-good)]"}`} data-testid="settings-msg">
            {msg}
          </div>
        )}
      </div>

      <PaymentMethodsCard />
      <StaleProgramsCard />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payment Methods card — list + delete capability for false detections
// ---------------------------------------------------------------------------
export function PaymentMethodsCard() {
  const { apiFetch } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await apiFetch("/api/admin/payment-methods"));
    } catch (e) { log.warn("[admin] payment-methods load:", e?.message); }
  }, [apiFetch]);
  useEffect(() => { load(); }, [load]);

  const remove = async (method) => {
    if (!confirm(`Remove "${method}" from ALL merchants? This can't be undone (but a re-scan will re-detect real ones).`)) return;
    setBusy(method); setMsg(null);
    try {
      const r = await apiFetch(`/api/admin/payment-methods/${encodeURIComponent(method)}`, { method: "DELETE" });
      setMsg(`Removed "${method}" from ${r.merchants_updated} merchants.`);
      await load();
    } catch (e) {
      setMsg(`Error: ${e?.message || e}`);
    } finally { setBusy(null); }
  };

  if (!data) return null;
  return (
    <div className="border border-[var(--srs-line)] bg-[var(--srs-surface)] rounded-lg p-6" data-testid="payment-methods-card">
      <div className="text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)] mb-2">PAYMENT METHODS</div>
      <h3 className="text-lg font-bold text-[var(--srs-ink)] mb-2">All detected payment methods · {data.methods.length}</h3>
      <p className="text-sm text-[var(--srs-ink-dim)] mb-4">
        Across {data.total_merchants} merchants. Delete any false detections — a fresh AI scan will pick up real ones again.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="payment-methods-grid">
        {data.methods.map((m) => (
          <div key={m.method} className="flex items-center justify-between gap-3 px-3 py-2 border border-[var(--srs-line)] rounded">
            <div className="min-w-0">
              <div className="text-sm font-medium text-[var(--srs-ink)] truncate">{m.method}</div>
              <div className="text-[10px] text-[var(--srs-ink-faint)] tracking-wider">{m.merchant_count} merchants</div>
            </div>
            <button onClick={() => remove(m.method)} disabled={busy === m.method}
                    className="text-[10px] tracking-[0.2em] font-bold uppercase px-2 py-1.5 border border-[var(--srs-line)] text-[var(--srs-ink-faint)] hover:text-[var(--srs-warn)] hover:border-[var(--srs-warn)] rounded disabled:opacity-50"
                    data-testid={`delete-method-${m.method}`}>
              {busy === m.method ? "…" : "Delete"}
            </button>
          </div>
        ))}
      </div>
      {msg && <div className={`mt-4 text-sm ${msg.startsWith("Error") ? "text-[var(--srs-warn)]" : "text-[var(--srs-good)]"}`} data-testid="payment-methods-msg">{msg}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stale Programs card — prune Trumf/SAS deals not seen recently
// ---------------------------------------------------------------------------
export function StaleProgramsCard() {
  const { apiFetch } = useAuth();
  const [program, setProgram] = useState("trumf");
  const [hours, setHours] = useState(72);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const prune = async () => {
    if (!confirm(`Prune ${program} deals older than ${hours}h? Removes the program sub-doc from merchants not seen in the last scrape.`)) return;
    setBusy(true); setResult(null);
    try {
      const r = await apiFetch(`/api/admin/programs/prune-stale?program=${program}&hours=${hours}`, { method: "POST" });
      setResult(r);
    } catch (e) {
      setResult({ error: e?.message || String(e) });
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-[var(--srs-line)] bg-[var(--srs-surface)] rounded-lg p-6" data-testid="stale-programs-card">
      <div className="text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)] mb-2">STALE DEAL CLEANUP</div>
      <h3 className="text-lg font-bold text-[var(--srs-ink)] mb-2">Prune deals not seen recently</h3>
      <p className="text-sm text-[var(--srs-ink-dim)] mb-4">
        When Trumf or SAS Online Shopping drops a merchant, the old entry stays in our DB. Run this after a fresh bulk scrape to remove anything that didn't refresh.
      </p>
      <div className="flex items-end gap-2 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.2em] font-bold text-[var(--srs-ink-faint)] mb-1">PROGRAM</div>
          <select value={program} onChange={(e) => setProgram(e.target.value)}
                  className="bg-[var(--srs-surface-2)] border border-[var(--srs-line)] text-[var(--srs-ink)] text-sm px-3 py-2 rounded outline-none"
                  data-testid="stale-program-select">
            <option value="trumf">Trumf Netthandel</option>
            <option value="sas">SAS Online Shopping</option>
          </select>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.2em] font-bold text-[var(--srs-ink-faint)] mb-1">OLDER THAN</div>
          <select value={hours} onChange={(e) => setHours(parseInt(e.target.value))}
                  className="bg-[var(--srs-surface-2)] border border-[var(--srs-line)] text-[var(--srs-ink)] text-sm px-3 py-2 rounded outline-none"
                  data-testid="stale-hours-select">
            <option value={24}>24 hours</option>
            <option value={72}>3 days (default)</option>
            <option value={168}>1 week</option>
            <option value={720}>30 days</option>
          </select>
        </div>
        <button onClick={prune} disabled={busy}
                className="px-5 py-2 text-[11px] tracking-[0.2em] font-bold uppercase bg-[var(--srs-warn)] text-white rounded disabled:opacity-50"
                data-testid="prune-stale-btn">
          {busy ? "Pruning…" : "Prune now →"}
        </button>
      </div>
      {result && (
        <div className="mt-4 p-4 border border-[var(--srs-line-soft)] bg-[var(--srs-surface-2)] rounded">
          {result.error ? (
            <div className="text-sm text-[var(--srs-warn)]" data-testid="prune-error">Error: {result.error}</div>
          ) : (
            <>
              <div className="text-sm text-[var(--srs-ink)] mb-2" data-testid="prune-result">
                Pruned <strong>{result.removed}</strong> stale {result.program} deals.
              </div>
              {result.hosts && result.hosts.length > 0 && (
                <details className="text-xs text-[var(--srs-ink-dim)]">
                  <summary className="cursor-pointer">View affected hosts ({result.hosts.length})</summary>
                  <div className="mt-2 font-mono text-[10px] grid grid-cols-2 md:grid-cols-3 gap-x-4">
                    {result.hosts.map((h) => <span key={h}>{h}</span>)}
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Lockouts Tab — owner-only. View/clear brute-force login lockouts.
// ---------------------------------------------------------------------------
